import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trophy, TrendingUp, Medal, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import { useNavigate } from "react-router-dom";

interface LeaderboardUser {
  id: string;
  username: string | null;
  avatar_url: string | null;
  total_wins: number;
  total_bids: number;
}

// Throttle interval for polling (30 seconds instead of realtime)
const POLL_INTERVAL_MS = 30000;

const Leaderboard = () => {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [users, setUsers] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLeaderboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    
    try {
      // Use secure function that only returns public profile data
      const { data, error } = await supabase.rpc("get_public_profiles", {
        limit_count: 50,
      });

      if (!error && data) {
        setUsers(data);
        setLastUpdated(new Date());
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchLeaderboard();

    // Poll every 30 seconds instead of realtime subscription
    // This reduces database load significantly at scale
    const pollInterval = setInterval(() => {
      fetchLeaderboard();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollInterval);
    };
  }, [fetchLeaderboard]);

  const handleManualRefresh = () => {
    fetchLeaderboard(true);
  };

  const getMedalColor = (rank: number) => {
    if (rank === 0) return "text-yellow-400";
    if (rank === 1) return "text-gray-300";
    if (rank === 2) return "text-amber-600";
    return "text-muted-foreground";
  };

  const getRankBg = (rank: number) => {
    if (rank === 0) return "bg-yellow-400/10 border-yellow-400/30";
    if (rank === 1) return "bg-gray-300/10 border-gray-300/30";
    if (rank === 2) return "bg-amber-600/10 border-amber-600/30";
    return "";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={18} className="mr-2" />
            {locale.language === "pt" ? "Voltar" : "Back"}
          </Button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <Trophy size={40} className="text-primary" />
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                {locale.language === "pt" ? "Classificação" : "Leaderboard"}
              </h1>
            </div>
            <p className="text-muted-foreground">
              {locale.language === "pt"
                ? "Os melhores previsores da plataforma"
                : "Top predictors on the platform"}
            </p>
          </motion.div>

          {/* Sort Info & Refresh */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex justify-center items-center gap-4 mb-8"
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50 border border-border/50">
              <Trophy size={16} className="text-primary" />
              <span className="text-sm text-muted-foreground">
                {locale.language === "pt" ? "Classificado por vitórias" : "Ranked by wins"}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="text-muted-foreground hover:text-foreground"
            >
              <RefreshCw size={16} className={`mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
              {locale.language === "pt" ? "Atualizar" : "Refresh"}
            </Button>
          </motion.div>

          {/* Last Updated */}
          {lastUpdated && (
            <div className="text-center mb-4">
              <span className="text-xs text-muted-foreground">
                {locale.language === "pt" ? "Última atualização: " : "Last updated: "}
                {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          )}

          {/* Leaderboard Table */}
          {loading ? (
            <div className="glass-card p-8 text-center text-muted-foreground">
              Loading...
            </div>
          ) : users.length === 0 ? (
            <div className="glass-card p-8 text-center text-muted-foreground">
              {locale.language === "pt"
                ? "Nenhum usuário encontrado"
                : "No users found"}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-3"
            >
              {users.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={`glass-card p-4 flex items-center gap-4 ${getRankBg(index)}`}
                >
                  {/* Rank */}
                  <div className="w-12 flex justify-center">
                    {index < 3 ? (
                      <Medal size={28} className={getMedalColor(index)} />
                    ) : (
                      <span className="text-lg font-bold text-muted-foreground">
                        #{index + 1}
                      </span>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.username || "User"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-bold text-primary">
                        {(user.username || "U")[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Username */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {user.username || `User ${user.id.slice(0, 6)}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user.total_bids} {locale.language === "pt" ? "previsões" : "predictions"}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-success font-bold">
                      <Trophy size={16} />
                      {user.total_wins}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Vitórias" : "Wins"}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Leaderboard;